import type React from "react";

import {
  BellPlusIcon,
  Calendar,
  Gift,
  MapPin,
  Ticket,
  Users,
  XCircle,
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  EventUpdatesType,
  useEventUpdates,
} from "@/pages/events/features/notifications/event-updates-context";

interface EventUpdateOption {
  type: EventUpdatesType;
  label: string;
  description: string;
  icon: typeof MapPin;
}

const eventUpdateOptions: EventUpdateOption[] = [
  {
    type: "event_added_v2",
    label: "Event Added",
    description: "Announce an event to artist & venue followers",
    icon: BellPlusIcon,
  },
  {
    type: "venue_changed",
    label: "Venue Changed",
    description: "When the curator changes the venue",
    icon: MapPin,
  },
  {
    type: "date_changed",
    label: "Date Changed",
    description: "When the curator changes the date",
    icon: Calendar,
  },
  {
    type: "lineup_added",
    label: "Lineup Added",
    description: "Select which artists to announce",
    icon: Users,
  },
  {
    type: "offer_added_v2",
    label: "Discount/Giveaway Added",
    description: "Announce offers",
    icon: Gift,
  },
  {
    type: "event_cancelled",
    label: "Event Cancelled",
    description: "Notify cancellation",
    icon: XCircle,
  },
  {
    type: "tickets_on_sale",
    label: "Tickets on Sale",
    description: "When ticketing is added and available",
    icon: Ticket,
  },
];

interface EventUpdatesOptionsDropdownProps {
  children: React.ReactNode;
  artists?: { id: string; name: string }[];
  followers?: number;
  thirdPartyFollowers?: number;
}

export function EventUpdatesOptionsDropdown({
  children,
  followers,
  thirdPartyFollowers,
}: EventUpdatesOptionsDropdownProps) {
  const {
    isOptionsOpen,
    openEventUpdatesOptions,
    closeEventUpdatesOptions,
    sendEventUpdates,
  } = useEventUpdates();

  const handleEventUpdatesSelect = (type: EventUpdatesType) => {
    sendEventUpdates(type);
  };

  return (
    <DropdownMenu
      open={isOptionsOpen}
      onOpenChange={(open) => {
        if (open) {
          openEventUpdatesOptions();
        } else {
          closeEventUpdatesOptions();
        }
      }}
    >
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent className="w-80">
        {eventUpdateOptions.map((option) => {
          const IconComponent = option.icon;
          return (
            <DropdownMenuItem
              key={option.type}
              onClick={() => handleEventUpdatesSelect(option.type)}
              className="flex items-start gap-3 p-3 cursor-pointer"
            >
              <IconComponent className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div className="flex flex-col gap-1">
                <span className="font-medium">{option.label}</span>
                <span className="text-sm text-muted-foreground">
                  {option.description} ~{" "}
                  {option.type === "event_added_v2"
                    ? thirdPartyFollowers
                    : followers}{" "}
                  people
                </span>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
